import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { FileText, Mail, Lock, Chrome, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { validateEmail } from '../utils/validation';
import { AppleLoader } from '@/components/ui/AppleLoader';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  const { login, signInWithGoogle, isAuthReady, currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '/';

  // After Google redirect returns successfully, leave the login page
  useEffect(() => {
    if (currentUser) {
      navigate(nextPath, { replace: true });
    }
  }, [currentUser, navigate, nextPath]);

  // Reset spinner when returning via browser Back (bfcache) after Google redirect
  useEffect(() => {
    const clearStuckLoading = () => {
      setLoading(false);
      try {
        sessionStorage.removeItem('resume_google_redirect');
      } catch {
        // ignore
      }
    };

    const onPageShow = (event) => {
      if (event.persisted || sessionStorage.getItem('resume_google_redirect')) {
        clearStuckLoading();
      }
    };

    window.addEventListener('pageshow', onPageShow);
    // Also clear if we landed back mid-redirect without a full remount
    if (sessionStorage.getItem('resume_google_redirect')) {
      clearStuckLoading();
    }

    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Real-time email validation
  useEffect(() => {
    if (email) {
      const validation = validateEmail(email);
      setEmailError(validation.valid ? '' : validation.message);
    } else {
      setEmailError('');
    }
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthReady) {
      toast.error('Sign-in is temporarily unavailable. Please try again later.');
      return;
    }
    
    // Validate email
    const emailValidation = validateEmail(email);
    setEmailError(emailValidation.valid ? '' : emailValidation.message);

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!emailValidation.valid) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      navigate(nextPath);
    } catch (error) {
      // Error is handled in AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!isAuthReady) {
      toast.error('Google sign-in is temporarily unavailable. Please try again later.');
      return;
    }
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      if (result) {
        navigate(nextPath);
      }
    } catch (error) {
      // Error is handled in AuthContext
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4">
      {/* Vibrant Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 -left-20 w-[500px] h-[500px] bg-gradient-to-br from-primary/10 via-primary/8 to-accent/10 rounded-full blur-3xl"
          animate={{
            x: [0, 80, 0],
            y: [0, -40, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-20 -right-20 w-[500px] h-[500px] bg-gradient-to-br from-accent/10 via-primary/8 to-primary/10 rounded-full blur-3xl"
          animate={{
            x: [0, -80, 0],
            y: [0, 40, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <motion.div
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="p-8 bg-white border border-primary/20 shadow-2xl shadow-primary/10 rounded-xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 pointer-events-none rounded-xl" />
          {/* Logo */}
          <div className="flex flex-col items-center mb-8 relative z-10">
            <motion.div
              className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary via-primary to-accent flex items-center justify-center shadow-lg shadow-primary/30 mb-4 relative overflow-hidden"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />
              <FileText className="w-8 h-8 text-white relative z-10" />
            </motion.div>
            <h1 className="text-3xl font-light bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent mb-2 tracking-tight">
              Resume Builder
            </h1>
            <p className="text-sm text-muted-foreground">Sign in to continue</p>
          </div>

          {!isAuthReady && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive relative z-10">
              Sign-in is temporarily unavailable. Please try again later.
            </div>
          )}

          {/* Google Sign In Button */}
          <Button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full mb-6 bg-white hover:bg-muted border border-border shadow-sm"
            variant="outline"
          >
            {loading ? (
              <AppleLoader size={16} label="Signing in" labelClassName="text-foreground" />
            ) : (
              <>
                <Chrome className="w-5 h-5 mr-2" />
                Continue with Google
              </>
            )}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${emailError ? 'text-destructive' : email && !emailError ? 'text-green-500' : 'text-muted-foreground'}`} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`pl-10 pr-10 ${emailError ? 'border-destructive focus-visible:ring-destructive' : email && !emailError ? 'border-green-500 focus-visible:ring-green-500' : ''}`}
                />
                {email && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {emailError ? (
                      <XCircle className="w-5 h-5 text-destructive" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                )}
              </div>
              {emailError && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  {emailError}
                </p>
              )}
              {email && !emailError && (
                <p className="text-xs text-green-500 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Valid email address
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                />
                {password && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                )}
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <AppleLoader size={16} tone="light" label="Signing in" labelClassName="text-white" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center text-sm space-y-2">
            <p>
              <span className="text-muted-foreground">Don't have an account? </span>
              <Link
                to={`/signup?next=${encodeURIComponent(nextPath)}`}
                className="text-primary hover:text-accent font-medium hover:underline"
              >
                Sign up
              </Link>
            </p>
            <p className="text-muted-foreground">
                Free SEO Tools by               <a
                href="https://www.elitesolutionusa.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
              > Elite Solution
              </a>
            </p>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default Login;

